import { useMachine } from '@xstate/react';
import React, { createContext, useState } from 'react';

import type { FeatureCategories } from 'src/modules/designStudio/constants';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import type { WindowItemType } from 'src/modules/designStudio/constants/hardCodedData/windowParametricData';
import type { TemplateWallAppearance } from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import {
  createMachine,
  initialContext,
} from 'src/modules/designStudio/machines/history';
import type {
  Context,
  ProductSelectedTrackingData,
} from 'src/modules/designStudio/machines/history/types';
import type { DesignStudioColor } from 'src/shared/graphql/generated';
import { useMode, useSplit, useTranslate } from 'src/shared/hooks';
import { useAuthExperience } from 'src/shared/hooks/useAuthExperience';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { useToaster } from 'src/shared/providers/Toaster';

import { Mode } from '../ModeProvider';

type HistoryProviderContextValueType = {
  historyState: Context;
  undoDesignChange: () => void;
  redoDesignChange: () => void;
  canRedo: boolean;
  canUndo: boolean;
  updateDesignConfigFromBuilding: () => void;
};

export const defaultContext: HistoryProviderContextValueType = {
  historyState: initialContext,
  undoDesignChange: () => undefined,
  redoDesignChange: () => undefined,
  canRedo: false,
  canUndo: false,
  updateDesignConfigFromBuilding: () => {},
};

export type UpdateDesignFunction = ({
  category,
  update,
  trackingData,
}: UpdateDesignProps) => void;

type UpdateDesignPropsTrackingData = Omit<
  ProductSelectedTrackingData,
  'design_studio_mode'
>;

interface UpdateDesignProps {
  selectedWindow?: WindowItemType;
  selectedTexture?: MenuItemType;
  selectedColor?: DesignStudioColor;
  category: FeatureCategories;
  update: {
    targetAppearanceGroup?: TemplateWallAppearance;
    textureId?: number | null;
    color?: string | null;
    gridStyle?: string | null;
    frameStyle?: string | null;
  };
  trackingData: UpdateDesignPropsTrackingData;
}

const context = createContext<HistoryProviderContextValueType>(defaultContext);

const ConnectedProvider = ({
  authToken,
  children,
  jobId,
  setIsNotConnectedToFirestore,
  shareToken,
  viewUserId,
}: {
  authToken?: string | null;
  children: React.ReactNode;
  jobId: number;
  setIsNotConnectedToFirestore: (isNotConnectedToFirestore: boolean) => void;
  shareToken?: string;
  viewUserId?: number;
}) => {
  const mode = useMode();
  const { addToast, removeToast } = useToaster();
  const translator = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { client: splitClient } = useSplit();
  const skipProfile = [Mode.GUEST, Mode.PREVIEW, Mode.SHARE].includes(mode);

  const [machineState, send] = useMachine(() =>
    createMachine({
      jobId,
      authToken,
      splitClient,
      shareToken,
      addToast,
      removeToast,
      translator,
      viewUserId,
      mode,
    }),
  );

  if (machineState.value === 'notConnectedToFirestore') {
    setIsNotConnectedToFirestore(true);
  }

  const { canRedo, canUndo } = machineState.context;
  useAuthExperience({ skipProfile, skipBranch: true });

  const undoDesignChange = () => send({ type: 'UNDO_DESIGN_CHANGE' });
  const redoDesignChange = () => {
    send({ type: 'REDO_DESIGN_CHANGE' });
  };

  const updateDesignConfigFromBuilding = () => {
    send({
      type: 'UPDATE_DESIGN_FROM_BUILDING',
    });
  };

  return (
    <context.Provider
      // See DS-846
      /* eslint-disable-next-line react/jsx-no-constructed-context-values */
      value={{
        historyState: machineState.context,
        undoDesignChange,
        redoDesignChange,
        canUndo,
        canRedo,
        updateDesignConfigFromBuilding,
      }}
    >
      {children}
    </context.Provider>
  );
};

const GuestHistoryProvider = ({ children }: { children: React.ReactNode }) => {
  const [historyState] = useState(defaultContext.historyState);

  const undoDesignChange = () => null;
  const redoDesignChange = () => null;

  return (
    <context.Provider
      // See DS-846
      /* eslint-disable-next-line react/jsx-no-constructed-context-values */
      value={{
        historyState,
        undoDesignChange,
        redoDesignChange,
        canUndo: false,
        canRedo: false,
        updateDesignConfigFromBuilding: () => {},
      }}
    >
      {children}
    </context.Provider>
  );
};

const Provider = ({
  mode,
  jobId,
  children,
  viewUserId,
  shareToken,
}: {
  mode: Mode;
  jobId?: number;
  children: React.ReactNode;
  viewUserId?: number;
  shareToken?: string;
}) => {
  const [isNotConnectedToFirestore, setIsNotConnectedToFirestore] =
    useState(false);

  if (
    mode === Mode.GUEST ||
    mode === Mode.PREVIEW ||
    isNotConnectedToFirestore
  ) {
    return <GuestHistoryProvider>{children}</GuestHistoryProvider>;
  }

  if (jobId) {
    return (
      <ConnectedProvider
        jobId={jobId}
        setIsNotConnectedToFirestore={setIsNotConnectedToFirestore}
        shareToken={shareToken}
        viewUserId={viewUserId}
      >
        {children}
      </ConnectedProvider>
    );
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

export { GuestHistoryProvider, ConnectedProvider, Provider, context };
